import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["preset", "question"]

  connect() {
    if (!this.hasPresetTarget) return

    this.maxInitializeAttempts = 100
    this.initializeAttempts = 0
    this.initializeRetryTimer = null
    this.reinitializeDelayTimer = null
    this.boundTurboLoadInit = () => this.reinitializeAfterTurboRender()
    this.boundTurboRenderInit = () => this.reinitializeAfterTurboRender()
    this.boundTurboFrameLoadInit = () => this.reinitializeAfterTurboRender()
    document.addEventListener("turbo:load", this.boundTurboLoadInit)
    document.addEventListener("turbo:render", this.boundTurboRenderInit)
    document.addEventListener("turbo:frame-load", this.boundTurboFrameLoadInit)

    this.initializePresetSelect2WithRetry()
    if (!this.hasQuestionTarget) return
    if (this.questionTarget.value.toString().trim().length > 0) return
    this.applyPreset()
  }

  disconnect() {
    this.clearInitializeRetryTimer()
    this.clearReinitializeDelayTimer()
    if (this.boundTurboLoadInit) {
      document.removeEventListener("turbo:load", this.boundTurboLoadInit)
      this.boundTurboLoadInit = null
    }
    if (this.boundTurboRenderInit) {
      document.removeEventListener("turbo:render", this.boundTurboRenderInit)
      this.boundTurboRenderInit = null
    }
    if (this.boundTurboFrameLoadInit) {
      document.removeEventListener("turbo:frame-load", this.boundTurboFrameLoadInit)
      this.boundTurboFrameLoadInit = null
    }
    this.destroyPresetSelect2()
  }

  applyPreset() {
    if (!this.hasPresetTarget || !this.hasQuestionTarget) return

    const selectedPrompts = Array.from(this.presetTarget.selectedOptions)
        .map((option) => String(option?.dataset?.prompt || "").trim())
        .filter((prompt) => prompt.length > 0)

    if (selectedPrompts.length === 0) return

    const combinedPrompt = [
      "Run one combined analysis that answers each selected query for the current filter combination:",
      ...selectedPrompts.map((prompt, index) => `${index + 1}. ${prompt}`)
    ].join("\n")

    this.questionTarget.value = combinedPrompt
    this.questionTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.questionTarget.focus()

    if (typeof this.questionTarget.setSelectionRange === "function") {
      const cursor = this.questionTarget.value.length
      this.questionTarget.setSelectionRange(cursor, cursor)
    }
  }

  reinitializeAfterTurboRender() {
    this.initializeAttempts = 0
    this.clearInitializeRetryTimer()
    this.clearReinitializeDelayTimer()
    this.initializePresetSelect2WithRetry()
    window.requestAnimationFrame(() => this.initializePresetSelect2WithRetry())
    this.reinitializeDelayTimer = window.setTimeout(() => {
      this.initializePresetSelect2WithRetry()
      this.reinitializeDelayTimer = null
    }, 120)
  }

  initializePresetSelect2WithRetry() {
    const initialized = this.initializePresetSelect2()
    if (initialized) {
      this.clearInitializeRetryTimer()
      return
    }

    if (this.initializeAttempts >= this.maxInitializeAttempts) return

    this.initializeAttempts += 1
    this.clearInitializeRetryTimer()
    this.initializeRetryTimer = window.setTimeout(() => this.initializePresetSelect2WithRetry(), 100)
  }

  clearInitializeRetryTimer() {
    if (!this.initializeRetryTimer) return
    window.clearTimeout(this.initializeRetryTimer)
    this.initializeRetryTimer = null
  }

  clearReinitializeDelayTimer() {
    if (!this.reinitializeDelayTimer) return
    window.clearTimeout(this.reinitializeDelayTimer)
    this.reinitializeDelayTimer = null
  }

  initializePresetSelect2() {
    const $ = window.jQuery || window.$
    if (!this.hasPresetTarget) return false
    if (!($ && typeof $.fn?.select2 === "function")) return false

    const $preset = $(this.presetTarget)
    if ($preset.data("select2")) return true

    try {
      $preset.select2({
        width: "100%",
        closeOnSelect: false,
        placeholder: this.presetTarget.dataset.placeholder || "Choose queries",
        allowClear: false,
        dropdownAutoWidth: false
      })
      return !!$preset.data("select2")
    } catch (_error) {
      return false
    }
  }

  destroyPresetSelect2() {
    const $ = window.jQuery || window.$
    if (!this.hasPresetTarget) return
    if (!($ && typeof $.fn?.select2 === "function")) return

    const $preset = $(this.presetTarget)
    if ($preset.data("select2")) $preset.select2("destroy")
  }
}